import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Trash2, Edit2, Check, Euro, Calendar, TrendingUp, TrendingDown,
  Users, Cloud, Sun, CloudRain, Download, ChevronLeft, ChevronRight,
  BarChart3, FileText, Loader2, Snowflake, CloudLightning, CloudFog
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useStandort } from "../hooks/useStandort";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WETTER_OPTIONEN = [
  { value: "sonnig", label: "Sonnig", icon: Sun },
  { value: "bewoelkt", label: "Bewoelkt", icon: Cloud },
  { value: "regen", label: "Regen", icon: CloudRain },
];

const WETTER_ICONS = {
  sonnig: Sun, bewoelkt: Cloud, regen: CloudRain,
  schnee: Snowflake, gewitter: CloudLightning, nebel: CloudFog
};

const ZEITRAUM_LABELS = {
  tag: "Tag", woche: "Woche", monat: "Monat",
  quartal: "Quartal", halbjahr: "Halbjahr", jahr: "Jahr"
};

const CHART_COLORS = ['#8B5CF6', '#3B82F6', '#06B6D4', '#10B981', '#F59E0B', '#EF4444', '#EC4899'];
const WT_COLORS = ['#64748b','#64748b','#64748b','#3B82F6','#10B981','#8B5CF6','#F59E0B'];

const formatCurrency = (value) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value || 0);

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  const dt = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
  return dt.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: '2-digit' });
};

const formatDateShort = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${d}.${m}.`;
};

export const Einnahmen = () => {
  const { activeStandortId } = useStandort();
  const [zeitraum, setZeitraum] = useState("monat");
  const [refDatum, setRefDatum] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  });
  const [analyse, setAnalyse] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUmsatz, setEditingUmsatz] = useState(null);
  const [formData, setFormData] = useState({
    datum: new Date().toISOString().split('T')[0],
    einnahmen_speisen: "", einnahmen_getraenke: "", einnahmen_sonstiges: "",
    gaeste_anzahl: "", wetter: "sonnig", notizen: ""
  });

  const fetchAnalyse = useCallback(async () => {
    setLoading(true);
    try {
      const standortParam = activeStandortId ? `&standort_id=${activeStandortId}` : '';
      const response = await axios.get(
        `${API}/einnahmen/analyse?zeitraum=${zeitraum}&datum=${refDatum}${standortParam}`,
        { withCredentials: true }
      );
      setAnalyse(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  }, [zeitraum, refDatum, activeStandortId]);

  useEffect(() => { fetchAnalyse(); }, [fetchAnalyse]);

  // Navigation vorwaerts/rueckwaerts
  const navigate = (direction) => {
    const [y, m, d] = refDatum.split('-').map(Number);
    let dt = new Date(y, m - 1, d);

    const offsets = {
      tag: 1, woche: 7, monat: 0, quartal: 0, halbjahr: 0, jahr: 0
    };

    if (zeitraum === "tag") {
      dt.setDate(dt.getDate() + direction);
    } else if (zeitraum === "woche") {
      dt.setDate(dt.getDate() + (direction * 7));
    } else if (zeitraum === "monat") {
      dt.setMonth(dt.getMonth() + direction);
    } else if (zeitraum === "quartal") {
      dt.setMonth(dt.getMonth() + (direction * 3));
    } else if (zeitraum === "halbjahr") {
      dt.setMonth(dt.getMonth() + (direction * 6));
    } else if (zeitraum === "jahr") {
      dt.setFullYear(dt.getFullYear() + direction);
    }

    const newDatum = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
    setRefDatum(newDatum);
  };

  const getZeitraumLabel = () => {
    if (!analyse) return '';
    const s = analyse.start;
    const e = analyse.end;
    if (zeitraum === "tag") return formatDate(s);
    if (zeitraum === "woche") return `${formatDateShort(s)} - ${formatDateShort(e)}`;
    if (zeitraum === "monat") {
      const [y, m] = s.split('-');
      const dt = new Date(parseInt(y), parseInt(m) - 1);
      return dt.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
    }
    if (zeitraum === "quartal") {
      const [y, m] = s.split('-');
      return `Q${Math.ceil(parseInt(m) / 3)} ${y}`;
    }
    if (zeitraum === "halbjahr") {
      const [y, m] = s.split('-');
      return `${parseInt(m) <= 6 ? '1.' : '2.'} Halbjahr ${y}`;
    }
    if (zeitraum === "jahr") {
      return s.split('-')[0];
    }
    return `${formatDateShort(s)} - ${formatDateShort(e)}`;
  };

  // Download
  const downloadExport = async (format) => {
    try {
      const response = await axios.get(
        `${API}/export/umsatzbericht?format=${format}&zeitraum=${zeitraum}&datum=${refDatum}`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Umsatzbericht_${zeitraum}_${refDatum}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${format.toUpperCase()} heruntergeladen!`);
    } catch (error) {
      toast.error("Download fehlgeschlagen");
    }
  };

  // CRUD
  const resetForm = () => {
    setFormData({
      datum: new Date().toISOString().split('T')[0],
      einnahmen_speisen: "", einnahmen_getraenke: "", einnahmen_sonstiges: "",
      gaeste_anzahl: "", wetter: "sonnig", notizen: ""
    });
    setEditingUmsatz(null);
  };

  const openEditDialog = (tag) => {
    setEditingUmsatz(tag);
    setFormData({
      datum: tag.datum,
      einnahmen_speisen: tag.speisen?.toString() || "",
      einnahmen_getraenke: tag.getraenke?.toString() || "",
      einnahmen_sonstiges: tag.sonstiges?.toString() || "",
      gaeste_anzahl: tag.gaeste?.toString() || "",
      wetter: tag.wetter || "sonnig",
      notizen: tag.notizen || ""
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.datum) { toast.error("Bitte Datum angeben"); return; }
    try {
      const payload = {
        ...formData,
        einnahmen_speisen: parseFloat(formData.einnahmen_speisen) || 0,
        einnahmen_getraenke: parseFloat(formData.einnahmen_getraenke) || 0,
        einnahmen_sonstiges: parseFloat(formData.einnahmen_sonstiges) || 0,
        gaeste_anzahl: formData.gaeste_anzahl ? parseInt(formData.gaeste_anzahl) : null,
        standort_id: editingUmsatz?.standort_id ?? activeStandortId ?? null,
      };
      if (editingUmsatz) {
        // Find the actual DB id from tagesumsatz
        const allItems = await axios.get(`${API}/tagesumsatz?monat=${formData.datum.substring(0, 7)}`, { withCredentials: true });
        const match = allItems.data.find(i => i.datum === formData.datum);
        if (match) {
          await axios.put(`${API}/tagesumsatz/${match.id}`, payload, { withCredentials: true });
          toast.success("Tagesumsatz aktualisiert");
        }
      } else {
        await axios.post(`${API}/tagesumsatz`, payload, { withCredentials: true });
        toast.success("Tagesumsatz erfasst");
      }
      setDialogOpen(false);
      resetForm();
      fetchAnalyse();
    } catch (error) {
      if (error.response?.status === 400) toast.error("Eintrag fuer dieses Datum existiert bereits");
      else toast.error("Fehler beim Speichern");
    }
  };

  const calculateGesamt = () => {
    return (parseFloat(formData.einnahmen_speisen) || 0) +
           (parseFloat(formData.einnahmen_getraenke) || 0) +
           (parseFloat(formData.einnahmen_sonstiges) || 0);
  };

  // Chart data for weekday analysis
  const wtChartData = analyse?.wochentag_analyse?.map((wt, i) => ({
    name: wt.tag, avg: wt.durchschnitt, fill: WT_COLORS[i]
  })) || [];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
      return (
        <div className="bg-slate-800 border border-slate-700 px-3 py-2 rounded-lg shadow-lg">
          <p className="text-white text-sm font-medium">{label}</p>
          <p className="text-green-400 font-mono">{formatCurrency(payload[0].value)}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="einnahmen-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Euro className="text-green-400" />
            Einnahmen
          </h1>
          <p className="text-slate-400 text-sm">Tagesumsaetze erfassen, analysieren & exportieren</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => { resetForm(); setDialogOpen(true); }}
            className="bg-green-600 hover:bg-green-700" data-testid="add-umsatz-btn">
            <Plus size={16} className="mr-2" /> Neuer Eintrag
          </Button>
        </div>
      </div>

      {/* Zeitraum-Tabs + Navigation */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
        <div className="flex flex-wrap gap-1 p-1 bg-slate-800/50 rounded-lg" data-testid="zeitraum-tabs">
          {Object.entries(ZEITRAUM_LABELS).map(([key, label]) => (
            <Button key={key} variant={zeitraum === key ? "default" : "ghost"}
              onClick={() => setZeitraum(key)} size="sm"
              className={zeitraum === key ? "bg-violet-600" : "text-slate-400"}
              data-testid={`zeitraum-${key}`}>
              {label}
            </Button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => navigate(-1)} className="border-slate-600 h-9 w-9" data-testid="nav-prev">
            <ChevronLeft size={16} />
          </Button>
          <div className="px-4 py-1.5 bg-slate-800 border border-slate-700 rounded-lg min-w-[180px] text-center" data-testid="zeitraum-label">
            <span className="text-white font-medium text-sm">{getZeitraumLabel()}</span>
          </div>
          <Button variant="outline" size="icon" onClick={() => navigate(1)} className="border-slate-600 h-9 w-9" data-testid="nav-next">
            <ChevronRight size={16} />
          </Button>

          {/* Download Buttons */}
          <div className="flex gap-1 ml-2">
            <Button variant="outline" size="sm" onClick={() => downloadExport("pdf")} className="border-slate-600 text-xs" data-testid="download-pdf">
              <Download size={14} className="mr-1" /> PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadExport("xlsx")} className="border-slate-600 text-xs" data-testid="download-xlsx">
              <FileText size={14} className="mr-1" /> Excel
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-violet-400" size={32} />
        </div>
      ) : analyse && analyse.tage > 0 ? (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
            <div className="p-4 bg-gradient-to-br from-green-900/40 to-green-800/20 border border-green-700/50 rounded-xl">
              <p className="text-xs text-green-400 mb-1">Gesamt</p>
              <p className="text-xl font-bold text-white">{formatCurrency(analyse.gesamt)}</p>
              <p className="text-[10px] text-slate-400 mt-1">{analyse.tage} Tage</p>
            </div>
            <div className="p-4 bg-slate-800/50 border border-slate-700 rounded-xl">
              <p className="text-xs text-slate-400 mb-1">Durchschnitt/Tag</p>
              <p className="text-xl font-bold text-white">{formatCurrency(analyse.durchschnitt_tag)}</p>
            </div>
            <div className="p-4 bg-slate-800/50 border border-slate-700 rounded-xl">
              <p className="text-xs text-emerald-400 mb-1">Speisen</p>
              <p className="text-lg font-bold text-white">{formatCurrency(analyse.speisen)}</p>
              <p className="text-[10px] text-slate-400">{analyse.gesamt > 0 ? ((analyse.speisen / analyse.gesamt) * 100).toFixed(0) : 0}%</p>
            </div>
            <div className="p-4 bg-slate-800/50 border border-slate-700 rounded-xl">
              <p className="text-xs text-violet-400 mb-1">Getraenke</p>
              <p className="text-lg font-bold text-white">{formatCurrency(analyse.getraenke)}</p>
              <p className="text-[10px] text-slate-400">{analyse.gesamt > 0 ? ((analyse.getraenke / analyse.gesamt) * 100).toFixed(0) : 0}%</p>
            </div>
            <div className="p-4 bg-slate-800/50 border border-slate-700 rounded-xl">
              <p className="text-xs text-slate-400 mb-1">Gaeste</p>
              <p className="text-lg font-bold text-white">{analyse.gaeste_gesamt}</p>
              <p className="text-[10px] text-slate-400">{analyse.gaeste_gesamt > 0 ? `${formatCurrency(analyse.gesamt / analyse.gaeste_gesamt)}/Gast` : ''}</p>
            </div>
            <div className="p-4 bg-slate-800/50 border border-slate-700 rounded-xl">
              <p className="text-xs text-slate-400 mb-1">Bester Tag</p>
              <p className="text-lg font-bold text-green-400">{formatCurrency(analyse.max_tag?.gesamt)}</p>
              <p className="text-[10px] text-slate-400">{formatDateShort(analyse.max_tag?.datum)}</p>
            </div>
          </div>

          {/* Wochentag-Chart + Wochen-Tabelle nebeneinander */}
          {zeitraum !== "tag" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Wochentag-Analyse Chart */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <BarChart3 size={16} className="text-violet-400" />
                  Durchschnitt nach Wochentag
                </h3>
                <div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={wtChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                      <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="avg" radius={[4, 4, 0, 0]}>
                        {wtChartData.map((entry, index) => (
                          <Cell key={index} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Wochen-Uebersicht */}
              {analyse.wochen_analyse?.length > 0 && (
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 max-h-[280px] overflow-y-auto">
                  <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <Calendar size={16} className="text-cyan-400" />
                    Kalenderwochen
                  </h3>
                  <div className="space-y-2">
                    {analyse.wochen_analyse.map((kw) => (
                      <div key={kw.kw} className="flex items-center justify-between p-2 bg-slate-700/30 rounded-lg">
                        <div>
                          <span className="text-white font-medium text-sm">{kw.kw}</span>
                          <span className="text-slate-500 text-xs ml-2">{formatDateShort(kw.start)} - {formatDateShort(kw.end)}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-green-400 font-mono text-sm">{formatCurrency(kw.summe)}</span>
                          <span className="text-slate-500 text-xs ml-2">({kw.tage}T)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tages-Tabelle */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden" data-testid="einnahmen-table">
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white">
                Tagesdetails ({analyse.tage_detail?.length || 0} Tage)
              </h3>
            </div>
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-700/50 sticky top-0">
                  <tr>
                    <th className="text-left p-3 text-slate-300 font-medium">Datum</th>
                    <th className="text-right p-3 text-slate-300 font-medium">Speisen</th>
                    <th className="text-right p-3 text-slate-300 font-medium">Getraenke</th>
                    <th className="text-right p-3 text-slate-300 font-medium">Sonstiges</th>
                    <th className="text-right p-3 text-slate-300 font-medium">Gesamt</th>
                    <th className="text-right p-3 text-slate-300 font-medium">Gaeste</th>
                    <th className="text-center p-3 text-slate-300 font-medium">Wetter</th>
                    <th className="text-left p-3 text-slate-300 font-medium">Notiz</th>
                  </tr>
                </thead>
                <tbody>
                  {analyse.tage_detail?.map((tag, i) => {
                    const WIcon = WETTER_ICONS[tag.wetter] || Cloud;
                    const isHigh = tag.gesamt > analyse.durchschnitt_tag * 1.2;
                    const isLow = tag.gesamt < analyse.durchschnitt_tag * 0.7;
                    return (
                      <tr key={tag.datum} className={`border-t border-slate-700/50 hover:bg-slate-700/30 cursor-pointer ${isHigh ? 'bg-green-900/10' : isLow ? 'bg-red-900/10' : ''}`}
                        onClick={() => openEditDialog(tag)}>
                        <td className="p-3 text-white font-medium">{formatDate(tag.datum)}</td>
                        <td className="p-3 text-right text-emerald-400 font-mono">{formatCurrency(tag.speisen)}</td>
                        <td className="p-3 text-right text-violet-400 font-mono">{formatCurrency(tag.getraenke)}</td>
                        <td className="p-3 text-right text-slate-500 font-mono">{formatCurrency(tag.sonstiges)}</td>
                        <td className={`p-3 text-right font-mono font-bold ${isHigh ? 'text-green-400' : isLow ? 'text-red-400' : 'text-white'}`}>
                          {formatCurrency(tag.gesamt)}
                        </td>
                        <td className="p-3 text-right text-slate-400">{tag.gaeste || '-'}</td>
                        <td className="p-3 text-center">
                          <WIcon size={16} className="inline text-slate-400" />
                        </td>
                        <td className="p-3 text-slate-500 text-xs max-w-[120px] truncate">{tag.notizen}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-20 bg-slate-800/30 rounded-xl border border-slate-700">
          <Calendar size={64} className="mx-auto text-slate-600 mb-4" />
          <h3 className="text-white text-lg font-semibold">Keine Eintraege fuer diesen Zeitraum</h3>
          <p className="text-slate-400 mt-2">Navigieren Sie zu einem anderen Zeitraum oder erfassen Sie neue Umsaetze</p>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="mt-4 bg-green-600 hover:bg-green-700">
            <Plus size={16} className="mr-2" /> Ersten Umsatz erfassen
          </Button>
        </div>
      )}

      {/* Eintrag-Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingUmsatz ? "Tagesumsatz bearbeiten" : "Neuer Tagesumsatz"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs text-slate-400">Datum *</Label>
                <Input type="date" value={formData.datum}
                  onChange={(e) => setFormData({...formData, datum: e.target.value})}
                  className="bg-slate-700 border-slate-600" disabled={!!editingUmsatz}
                  data-testid="umsatz-datum-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-slate-400">Wetter</Label>
                <Select value={formData.wetter} onValueChange={(v) => setFormData({...formData, wetter: v})}>
                  <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {WETTER_OPTIONEN.map((w) => (
                      <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-slate-400">Speisen</Label>
                <Input type="number" step="0.01" value={formData.einnahmen_speisen}
                  onChange={(e) => setFormData({...formData, einnahmen_speisen: e.target.value})}
                  placeholder="0.00" className="bg-slate-700 border-slate-600 font-mono" data-testid="umsatz-speisen-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-slate-400">Getraenke</Label>
                <Input type="number" step="0.01" value={formData.einnahmen_getraenke}
                  onChange={(e) => setFormData({...formData, einnahmen_getraenke: e.target.value})}
                  placeholder="0.00" className="bg-slate-700 border-slate-600 font-mono" data-testid="umsatz-getraenke-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-slate-400">Sonstiges</Label>
                <Input type="number" step="0.01" value={formData.einnahmen_sonstiges}
                  onChange={(e) => setFormData({...formData, einnahmen_sonstiges: e.target.value})}
                  placeholder="0.00" className="bg-slate-700 border-slate-600 font-mono" data-testid="umsatz-sonstiges-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-slate-400">Gaeste</Label>
                <Input type="number" value={formData.gaeste_anzahl}
                  onChange={(e) => setFormData({...formData, gaeste_anzahl: e.target.value})}
                  placeholder="z.B. 85" className="bg-slate-700 border-slate-600 font-mono" data-testid="umsatz-gaeste-input" />
              </div>
              <div className="col-span-2 space-y-2">
                <Label className="text-xs text-slate-400">Notizen</Label>
                <Input value={formData.notizen} onChange={(e) => setFormData({...formData, notizen: e.target.value})}
                  placeholder="z.B. Event, Feiertag" className="bg-slate-700 border-slate-600" data-testid="umsatz-notizen-input" />
              </div>
            </div>

            <div className="p-4 bg-slate-900/50 rounded-lg">
              <div className="flex justify-between text-sm text-slate-400">
                <span>Speisen:</span><span className="font-mono">{formatCurrency(parseFloat(formData.einnahmen_speisen) || 0)}</span>
              </div>
              <div className="flex justify-between text-sm text-slate-400">
                <span>Getraenke:</span><span className="font-mono">{formatCurrency(parseFloat(formData.einnahmen_getraenke) || 0)}</span>
              </div>
              <div className="flex justify-between text-sm text-slate-400">
                <span>Sonstiges:</span><span className="font-mono">{formatCurrency(parseFloat(formData.einnahmen_sonstiges) || 0)}</span>
              </div>
              <div className="border-t border-slate-700 mt-2 pt-2 flex justify-between text-white font-bold">
                <span>Tagesumsatz:</span><span className="text-green-400 font-mono">{formatCurrency(calculateGesamt())}</span>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-600">Abbrechen</Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700" data-testid="save-umsatz-btn">
                <Check size={16} className="mr-2" /> {editingUmsatz ? "Aktualisieren" : "Erfassen"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Einnahmen;
